import {CustomFieldResourceTypeEnum} from '@frontapp/workflows/custom_fields';
import React, {FC, useMemo} from 'react';
import styled from 'styled-components';

import {useEventHandler} from '../../../../../../../libs/shared/core/src/helpers/react/hooks/useEventHandler';
import {wrapInShallowSelector} from '../../../../../../../srcCommon/helpers/reselectHelpers';
import {useAppSelector} from '../../../../../../helpers/appReduxHelpers';
import {getFlatCustomFields} from '../../../../../../store/data/dataSelectors';
import {ConversationCustomFieldNameField} from './conversationCustomFieldNameField';
import {CustomAttributeValueEditor} from './customAttributeValueEditor';

/*
 * Props.
 */

export interface ConversationCustomFieldFieldProps {
  selectedCustomFieldName: string | undefined;
  selectedCustomFieldValue: string | undefined;
  onChange: (arg: {name: string; value: string | undefined} | undefined) => void;
}

/*
 * Styles.
 */

const StyledButtonContentDiv = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  width: 100%;
`;

/*
 * Components.
 */

export const ConversationCustomFieldField: FC<ConversationCustomFieldFieldProps> = (props) => {
  const {selectedCustomFieldName, selectedCustomFieldValue, onChange} = props;

  const customFields = useAppSelector(wrapInShallowSelector(getFlatCustomFields));
  const conversationCustomFields = useMemo(
    () =>
      customFields.filter(
        (customField) => customField.resourceType === CustomFieldResourceTypeEnum.CONVERSATION,
      ),
    [customFields],
  );

  const selectedCustomField = useMemo(() => {
    if (!selectedCustomFieldName) {
      return undefined;
    }

    // We need to find the id of the custom field.
    return conversationCustomFields.find((customField) => customField.name === selectedCustomFieldName);

    // FIXME(ldemarne): What if we can't find the selected custom field?
  }, [conversationCustomFields, selectedCustomFieldName]);

  return (
    <StyledButtonContentDiv>
      <ConversationCustomFieldNameField
        selectedCustomField={selectedCustomField}
        conversationCustomFields={conversationCustomFields}
        onChange={useEventHandler((value) => {
          onChange(value ? {name: value.name, value: undefined} : undefined);
        })}
      />
      {selectedCustomField && (
        <CustomAttributeValueEditor
          customAttribute={{customField: selectedCustomField, value: selectedCustomFieldValue ?? ''}}
          editedValue={selectedCustomFieldValue ?? ''}
          onEdit={(newValue) => {
            onChange({name: selectedCustomField.name, value: newValue || undefined});
          }}
          onEditSave={(newValue) => {
            onChange({name: selectedCustomField.name, value: newValue || undefined});
          }}
          onEditCancel={() => {}}
        />
      )}
    </StyledButtonContentDiv>
  );
};
